// hooks
import { useCallback, useEffect, useState, Fragment } from "react";
import useHttp from "hooks/use-http";
import { useNavigate } from "react-router-dom";

import { useForm, Controller } from "react-hook-form";

// material styles
import {
  Grid,
  IconButton,
  Tooltip,
  TextField,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material";

// material icons
import { Delete, Edit, FilterAlt } from "@mui/icons-material";

// components
import LoadingCM from "components/shared/Loading";
import DrawerCM from "components/shared/Drawer";
import TableCM from "components/shared/Table";
import PaginationCM from "components/shared/Pagination";

// styled component
import { StyledStack, StyledMenuItem, StyledGridIcons } from "./services.style";

// helper
import GenerateHeadCell from "helper/generate-head-cell";

// constants
import { PAGINATION_LIMIT } from "constants";

// request methods
import {
  getServiceSubCategoryRequest,
  getServicesDataRequest,
} from "api/requests/services";

// products head cells
const headCells = GenerateHeadCell("services_list");

// services categories...
const categoryList = [
  { value: "consultation", title: "مشاوره" },
  { value: "performance", title: "اجرا" },
  { value: "supervision", title: "نظارت" },
  { value: "export-import-clearance", title: "صادرات واردات و ترخیص" },
];

function DisplayServices() {
  const { request, loading, setLoading } = useHttp(false);
  const {
    register,
    control,
    formState: { errors },
  } = useForm();
  const navigate = useNavigate();

  // states for filtered
  const [searchValue, setSearchValue] = useState("");

  // services data state
  const [servicesData, setServicesData] = useState({
    data: [],
    count: 0,
  });

  // state for filter
  const [selectedCategory, setSelectedCategory] = useState("");
  const [allSubCategories, setAllSubCategories] = useState({
    data: [],
    count: 0,
  });
  const [selectedSubCategory, setSelectedSubCategory] = useState("");
  const [subCategoryList, setSubCategoryList] = useState({
    data: [],
    count: 0,
  });

  // page state for pagination
  const [page, setPage] = useState(1);

  // get all services data
  const getServicesData = async () => {
    setLoading(true);

    const subCategoriesRes = await request({
      url: getServiceSubCategoryRequest,
      values: {
        skip: 0,
        limit: 100,
        search: null,
      },
      isMulty: true,
    });
    if (subCategoriesRes) {
      setAllSubCategories({
        data: subCategoriesRes.data.data,
        count: subCategoriesRes.data.count,
      });
    }

    const values = {
      skip: searchValue ? 0 : (page - 1) * PAGINATION_LIMIT,
      limit: searchValue ? 100 : PAGINATION_LIMIT,
      search: searchValue ?? null,
      category: selectedCategory ?? null,
      sub_category: selectedSubCategory ?? null,
    };

    !searchValue.length && delete values.search;
    !selectedCategory && delete values.category;
    !selectedSubCategory && delete values.sub_category;

    const servicesRes = await request({
      url: getServicesDataRequest,
      values,
      isMulty: true,
    });
    setLoading(false);

    if (servicesRes) {
      if (servicesRes.data.count < 1) {
        return setServicesData({
          count: servicesRes.data.count,
          data: [{ content: "هیچ اطلاعاتی یافت نشد" }],
        });
      }

      let transformedData = servicesRes.data.data.map((items) => {
        // transformed category name
        if (items.services_sub_categories) {
          const [{ value, title: transformedCategory }] = categoryList.filter(
            (item) => item.value === items.services_sub_categories.category
          );

          return {
            id: items.id,
            title: items.title,
            category: transformedCategory,
            subcategory: items.services_sub_categories.title,
            edit: (
              <IconButton
                sx={{ color: "#1B569A" }}
                onClick={() => {
                  navigate("/services/edit/" + items.id);
                }}
              >
                <Tooltip title="ویرایش" placement="top">
                  <Edit />
                </Tooltip>
              </IconButton>
            ),
          };
        }
        return {
          id: items.id,
          title: items.title,
          category: "ثبت نشده",
          subcategory: "ثبت نشده",
          edit: (
            <IconButton
              sx={{ color: "#1B569A" }}
              onClick={() => {
                navigate("/services/edit/" + items.id);
              }}
            >
              <Tooltip title="ویرایش" placement="top">
                <Edit />
              </Tooltip>
            </IconButton>
          ),
        };
      });
      setServicesData({
        data: transformedData,
        count: servicesRes.data.count,
      });
    }

    setLoading(false);
  };

  // get services without filter
  const getServicesDataWithoutFilter = async () => {
    setSelectedCategory("");
    setSelectedSubCategory("");
    setSearchValue("");

    const servicesRes = await request({
      url: getServicesDataRequest,
      values: {
        skip: (page - 1) * PAGINATION_LIMIT,
        limit: PAGINATION_LIMIT,
      },
      isMulty: true,
    });
    setLoading(false);

    if (servicesRes) {
      if (servicesRes.data.count < 1) {
        return setServicesData({
          count: servicesRes.data.count,
          data: [{ content: "هیچ اطلاعاتی یافت نشد" }],
        });
      }

      let transformedData = servicesRes.data.data.map((items) => {
        // transformed category name
        if (items.services_sub_categories) {
          const [{ value, title: transformedCategory }] = categoryList.filter(
            (item) => item.value === items.services_sub_categories.category
          );

          return {
            id: items.id,
            title: items.title,
            category: transformedCategory,
            subcategory: items.services_sub_categories.title,
            edit: (
              <IconButton
                sx={{ color: "#1B569A" }}
                onClick={() => {
                  navigate("/services/edit/" + items.id);
                }}
              >
                <Tooltip title="ویرایش" placement="top">
                  <Edit />
                </Tooltip>
              </IconButton>
            ),
          };
        }
        return {
          id: items.id,
          title: items.title,
          category: "ثبت نشده",
          subcategory: "ثبت نشده",
          edit: (
            <IconButton
              sx={{ color: "#1B569A" }}
              onClick={() => {
                navigate("/services/edit/" + items.id);
              }}
            >
              <Tooltip title="ویرایش" placement="top">
                <Edit />
              </Tooltip>
            </IconButton>
          ),
        };
      });
      setServicesData({
        data: transformedData,
        count: servicesRes.data.count,
      });
    }

    setLoading(false);
  };

  useEffect(() => {
    setSelectedSubCategory(null);
    let data = allSubCategories.data.filter(
      (item) => item.category == selectedCategory
    );

    let transformedData = data.map((item) => ({
      id: item.id,
      title: item.title,
    }));
    setSubCategoryList({
      data: transformedData,
      count: transformedData.length,
    });
  }, [selectedCategory]);

  useEffect(() => {
    getServicesData();
  }, [page]);
  return (
    <DrawerCM title="خدمات">
      {loading ? (
        <LoadingCM />
      ) : (
        <Fragment>
          <StyledStack>
            <Grid container flex alignItems="center">
              <Grid item sm={4} xs={12} p={0.5}>
                <TextField
                  label="جست و جو"
                  fullWidth
                  id="outlined-size-small"
                  size="small"
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                />
              </Grid>
              <Grid item sm={3} xs={12} p={0.5}>
                <Controller
                  control={control}
                  defaultValue={""}
                  name="select_category"
                  render={() => (
                    <FormControl fullWidth size="small">
                      <InputLabel id="select_category">سردسته</InputLabel>
                      <Select
                        id="category"
                        name="category"
                        fullWidth
                        label="سردسته"
                        labelId="select-category"
                        value={selectedCategory ?? ""}
                        {...register("category")}
                        error={Boolean(errors.category)}
                        helperText={errors.category?.message}
                        onChange={(e) => setSelectedCategory(e.target.value)}
                      >
                        {categoryList.map((item, index) => (
                          <StyledMenuItem key={index} value={item.value}>
                            {item.title}
                          </StyledMenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid item sm={3} xs={12} p={0.5}>
                <Controller
                  control={control}
                  defaultValue={""}
                  name="select_subcategory"
                  render={() => (
                    <FormControl fullWidth size="small">
                      <InputLabel id="select_subcategory">زیردسته</InputLabel>
                      <Select
                        id="subcategory"
                        name="subcategory"
                        label="زیردسته"
                        fullWidth
                        labelId="select_subcategory"
                        value={selectedSubCategory ?? ""}
                        {...register("subcategory")}
                        error={Boolean(errors.subcategory)}
                        helperText={errors.subcategory?.message}
                        onChange={(e) => setSelectedSubCategory(e.target.value)}
                      >
                        {subCategoryList?.data?.map((item, index) => (
                          <StyledMenuItem key={index} value={item.id}>
                            {item.title}
                          </StyledMenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>
              <StyledGridIcons item sm={2} xs={12} p={0.5}>
                <IconButton color="primary" onClick={getServicesData}>
                  <Tooltip title="اعمال فیلتر" placement="top">
                    <FilterAlt />
                  </Tooltip>
                </IconButton>
                <IconButton
                  color="error"
                  onClick={getServicesDataWithoutFilter}
                >
                  <Tooltip title="حذف فیلتر" placement="top">
                    <Delete />
                  </Tooltip>
                </IconButton>
              </StyledGridIcons>
            </Grid>
          </StyledStack>
          <Grid container>
            <Grid item xs={12} my={2}>
              <TableCM
                title="لیست خدمات"
                rows={servicesData.data}
                headCells={headCells}
                page={page}
                addServices
                addServicesSubCategory
                colspan={servicesData.count === 0 ? "11" : ""}
                fontSize="12px"
                cellFontSize="12px"
              />
            </Grid>
            {servicesData.count < 1 ? (
              " "
            ) : (
              <PaginationCM
                handleChange={(_, val) => setPage(val)}
                page={page}
                count={servicesData.count}
              />
            )}
          </Grid>
        </Fragment>
      )}
    </DrawerCM>
  );
}

export default DisplayServices;
